import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import { trackViewItem } from "@/lib/analytics";

const SWATCH_MAP = {
  Black: "#111111",
  Brown: "#4a2f21",
  Cognac: "#a05a2c",
  Tan: "#c99a6b",
  Beige: "#d8c9a3",
  Ivory: "#f4ecd8",
  White: "#ffffff",
  Navy: "#1c2c4a",
  Burgundy: "#5a1a1f",
  Maroon: "#7a1e28",
  Grey: "#7a7a7a",
  Olive: "#5a5a30",
  Gold: "#c8a24b",
};
const swatchFor = (name) => SWATCH_MAP[name] || "#8c7a6b";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const { addItem } = useCart();

  useEffect(() => {
    api.get(`/products/${id}`).then(({ data }) => {
      setProduct(data);
      trackViewItem(data);
    });
  }, [id]);

  if (!product) return <div className="pt-32 px-6 text-sm text-[#666]">Loading…</div>;

  const availableColors = (product.colors && product.colors.length > 0) ? product.colors : [];

  const handleAdd = () => {
    if (!size) {
      toast.error("Please select a size");
      return;
    }
    if (availableColors.length > 0 && !color) {
      toast.error("Please select a colour");
      return;
    }
    addItem(product, size, 1, color || null);
    toast.success(`${product.name}${color ? ` (${color})` : ""} added to bag`);
  };

  return (
    <div className="pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20">
        <div className="aspect-[4/5] bg-[#f5f5f5] overflow-hidden">
          <img
            src={product.image_url}
            alt={product.name}
            className="w-full h-full object-cover"
          />
        </div>

        <div className="flex flex-col">
          <div className="label-eyebrow mb-4">{product.category}</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
            {product.name}
          </h1>
          <div className="mt-6 text-xl font-medium" data-testid="product-price">
            ₹{product.price.toFixed(2)}
          </div>

          {(product.colors && product.colors.length > 0) ? (
            <div className="mt-8" data-testid="product-colors">
              <div className="label-eyebrow mb-4">Select colour</div>
              <div className="flex flex-wrap gap-2">
                {product.colors.map((c) => (
                  <button
                    key={c}
                    onClick={() => setColor(c)}
                    className={`inline-flex items-center gap-2 px-4 py-3 border text-sm transition-colors ${
                      color === c
                        ? "border-[#111] bg-[#111] text-white"
                        : "border-[#e5e5e5] hover:border-[#111]"
                    }`}
                    data-testid={`color-option-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <span
                      className="inline-block w-4 h-4 rounded-full border border-[#e5e5e5]"
                      style={{ background: swatchFor(c) }}
                    />
                    {c}
                  </button>
                ))}
              </div>
            </div>
          ) : product.color ? (
            <div className="mt-3 text-sm text-[#666]" data-testid="product-color">
              Colour · {product.color}
            </div>
          ) : null}

          <p className="mt-10 text-[#666] leading-relaxed max-w-md">{product.description}</p>

          <div className="mt-10">
            <div className="label-eyebrow mb-4">Select size (UK)</div>
            <div className="grid grid-cols-5 gap-2 max-w-sm">
              {product.sizes.map((s) => (
                <button
                  key={s}
                  onClick={() => setSize(s)}
                  className={`py-3 border text-sm transition-colors ${
                    size === s
                      ? "border-[#111] bg-[#111] text-white"
                      : "border-[#e5e5e5] hover:border-[#111]"
                  }`}
                  data-testid={`size-${s}`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-10 flex flex-col gap-3 max-w-sm">
            <button
              onClick={handleAdd}
              className="bg-[#111] text-white px-8 py-4 text-sm font-medium hover:bg-[#333] transition-colors"
              data-testid="add-to-cart-button"
            >
              Add to Bag
            </button>
            <div className="text-xs text-[#666] mt-2">
              Free shipping on all orders · Complimentary returns within 30 days.
            </div>
          </div>

          <div className="mt-12 border-t border-[#e5e5e5] pt-8 space-y-4 text-sm">
            {product.color && (
              <div className="flex justify-between gap-4">
                <span className="text-[#666] flex-shrink-0">Colour in photo</span>
                <span className="text-right">{product.color}</span>
              </div>
            )}
            <div className="flex justify-between gap-4">
              <span className="text-[#666] flex-shrink-0">Material</span>
              <span className="text-right">{product.material || "Premium synthetic"}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-[#666] flex-shrink-0">Construction</span>
              <span className="text-right">{product.construction || "Hand-stitched"}</span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-[#666] flex-shrink-0">Origin</span>
              <span className="text-right">{product.origin || "Agra, India"}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
